import { useEffect } from 'react';
import { Beaker, X } from 'lucide-react';
import { ProductImage } from './ProductImage';
import { Button } from '@/components/ui/button';
import { displayName, type CatalogItem } from '@/types/domain';
import { formatUsd } from '@/lib/format';

interface ProductSpecsDrawerProps {
  item: CatalogItem | null;
  unitPrice: number;
  onClose: () => void;
}

/**
 * Calibrated finished volume for BIB-format syrups at a 5:1 dilution.
 * Sourced from Sky — industry-standard yields, not a clean ratio×capacity
 * multiplication.
 */
function finishedAt5to1(category: CatalogItem['category']): number | null {
  if (
    category === '3-gal BIB' ||
    category === 'Sugar Free' ||
    category === 'Mixer'
  ) {
    return 1536;
  }
  if (category === '5-gal BIB') {
    return 3780;
  }
  return null;
}

export function ProductSpecsDrawer({
  item,
  unitPrice,
  onClose,
}: ProductSpecsDrawerProps) {
  // Close on Escape
  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!item) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [item]);

  if (!item) return null;

  const name = displayName(item);
  const finishedOz = finishedAt5to1(item.category);
  const finishedGal = finishedOz !== null ? (finishedOz / 128).toFixed(1) : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="specs-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-brix-ink/40 backdrop-blur-sm"
      />

      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-brix-surface rounded-2xl border border-brix-border shadow-card-hover">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 bg-brix-surface/95 backdrop-blur-md border-b border-brix-border">
          <h2 id="specs-title" className="font-display text-lg font-semibold text-brix-ink truncate">
            {name}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-md flex items-center justify-center text-brix-muted hover:text-brix-ink hover:bg-brix-surface-alt transition-colors duration-150 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Big BIB image — or fall back to the seal if no BIB photo. */}
          <div className="flex justify-center bg-brix-surface-alt rounded-xl p-4">
            <ProductImage
              imageUrl={item.bibImageUrl ?? item.imageUrl}
              accent={item.accentColor ?? '#6A7780'}
              category={item.category}
              alt={`${name} BIB box`}
              size="lg"
              className="!w-40 !h-40"
            />
          </div>

          {/* Identity */}
          <div className="grid grid-cols-2 gap-3">
            <SpecRow label="Category" value={item.category} />
            <SpecRow label="SKU" value={item.sku ?? '—'} mono />
            <SpecRow label="Pack" value={item.packSize ?? '—'} />
            <SpecRow label="Container" value={item.containerType ?? '—'} />
            <SpecRow
              label="Volume"
              value={item.volumeOz !== null ? `${formatNumber(item.volumeOz)} fl oz` : '—'}
              mono
            />
            <SpecRow
              label="Weight"
              value={item.weightLbs !== null ? `${formatNumber(item.weightLbs)} lbs` : '—'}
              mono
            />
            <SpecRow label="Dimensions" value={item.dimensionsIn ?? '—'} mono full />
          </div>

          {/* Finished volume math for BIBs */}
          {finishedOz !== null && (
            <div className="rounded-xl border border-brix-border bg-brix-primary-tint p-4">
              <p className="text-[10px] tracking-tight text-brix-primary font-semibold flex items-center gap-1.5">
                <Beaker size={12} /> Finished volume (5:1 mix)
              </p>
              <p className="mt-2 font-display text-2xl font-semibold font-mono text-brix-primary">
                {finishedOz.toLocaleString()} fl oz
              </p>
              <p className="text-xs text-brix-primary/80 mt-0.5">
                ~{finishedGal} finished gallons per BIB
              </p>
            </div>
          )}

          {/* Pricing */}
          <div className="flex items-baseline justify-between gap-2 pt-1 border-t border-brix-border">
            <div>
              <p className="text-[10px] tracking-tight text-brix-muted font-semibold">
                Your price
              </p>
              <p className="font-display text-2xl font-semibold font-mono text-brix-ink">
                {formatUsd(unitPrice)}
              </p>
            </div>
            {unitPrice < item.listPrice && (
              <div className="text-right">
                <p className="text-[10px] tracking-tight text-brix-muted font-semibold">
                  List
                </p>
                <p className="text-sm font-mono line-through text-brix-muted">
                  {formatUsd(item.listPrice)}
                </p>
              </div>
            )}
          </div>

          <Button type="button" variant="secondary" onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function SpecRow({
  label,
  value,
  mono,
  full,
}: {
  label: string;
  value: string;
  mono?: boolean;
  full?: boolean;
}) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <p className="text-[10px] tracking-tight text-brix-muted font-semibold">
        {label}
      </p>
      <p
        className={
          'mt-0.5 text-sm text-brix-ink ' + (mono ? 'font-mono' : 'font-medium')
        }
      >
        {value}
      </p>
    </div>
  );
}

function formatNumber(n: number): string {
  return Number.isInteger(n) ? n.toString() : n.toFixed(1);
}
